/* eslint-disable no-debugger */
import { Fragment } from "react";
import StandardKendoReport from "@usas/ui-web/components/embeddedReports/kendoReports/StandardKendoReport";
import PopUpModal from "@usas/ui-web/components/dialogs/PopUpModal";
import useMeetDetailKendoReport from "./UseMeetDetailKendoReport";
import PrimaryButton from "@usas/ui-web/components/buttons/PrimaryButton";
import ZoneMultiSelect from "@usas/ui-web/components/multiSelect/zoneMultiSelect/ZoneMultiSelect";
import LscMultiSelect from "@usas/ui-web/components/multiSelect/lscMultiSelect/LscMultiSelect";
import ClubMultiSelect from "@usas/ui-web/components/multiSelect/clubMultiSelect/ClubMultiSelect";
import MeetClassificationMultiSelect from "@usas/ui-web/components/multiSelect/meetClassificationiMultiSelect/MeetClassificationMultiSelect";  
import MeetTypeMultiSelect from "@usas/ui-web/components/multiSelect/meetTypeMultiSelect/MeetTypeMultiSelect";

import Dropdown from "@usas/ui-web/components/dropdowns/Dropdown";
import YesNoSwitch from "@usas/ui-web/components/yesNoSwitch/YesNoSwitch";
import Headings from "@usas/ui-web/components/headings/Headings";
import QuestionIcon from "@usas/ui-web/components/icons/QuestionIcon";
import SecondaryButton from "@usas/ui-web/components/buttons/SecondaryButton";
import DatePicker from "@usas/ui-web/components/datepickers/DatePicker";
import CourseMultiSelect from "@usas/ui-web/components/multiSelect/courseMultiSelect/CourseMultiSelect";
import OrgUnitDropdown from "../../components/dropdowns/orgUnitDropdown/OrgUnitDropdown";
import style from "../kendoReports/shared/KendoReport.module.css";

// NOTE: if you have are using basic single select parameters like DDLs and textboxes you can dynamically gerenerate the parametes from json.
// see ClubTimesNew.jsx. If you want to use a specic complex component like EventMultiSelect you can create a form as in this example.

const MeetDetailKendoReport = () => {
  const {
    formState,
    errorState,
    reportParameters,
    parameters,
    columns,
    modalState,
    orgUnitState,
    onModalCancelClicked,
    handleSubmit,
    onFormValueChanged,
    onValueTextPairChanged,
    updateFormState,
    onRunReportClicked,
    onClearForm,
    seasonYearState,
    excelColumns,
    clubFilterIds,
    REPORT_INFO_ID
  } = useMeetDetailKendoReport();

  return (
    <Fragment>
      <form onSubmit={handleSubmit} noValidate>
        <div className="row">
          <div className="col-xs-12">
            <Headings.H3>Meet Detail Report</Headings.H3>
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12 col-sm-6 col-md-3">
            <OrgUnitDropdown 
              label="Organizations*"
              name="orgUnitId"
              value={formState.orgUnitId}
              error={errorState.orgUnitId}  
              message={errorState.orgUnitId}
              reportInfoId={REPORT_INFO_ID}
              onChange={(newValue, newValueLabel, e) => { 
                onValueTextPairChanged(newValue, 'orgUnitId', newValueLabel, 'orgUnitName', e); 
                onFormValueChanged('zones', []);
                onFormValueChanged('lscs', []);
                onFormValueChanged('clubs', []);
              }}
            />
          </div>
        </div> 
        <div className="row">
          {(orgUnitState.arrayData.find(ou => ou.orgUnitId === formState.orgUnitId)?.orgLevelId === 12 || formState.orgUnitId === '') &&
          <>
          <div className="col-xs-12 col-sm-6 col-md-4">
            <ZoneMultiSelect
              label={<span>Zones&nbsp;&nbsp;<QuestionIcon toolTipText="Leave blank to include all zones." /></span>}
              name="zones"
              value={formState.zones}
              error={errorState.zones}
              message={errorState.zones}
              onChange={(value) => {
                onFormValueChanged('zones', value);
                onFormValueChanged('lscs', []);
                onFormValueChanged('clubs', []);
              }} />
          </div>
          <div className="col-xs-12 col-sm-6 col-md-4">
            <LscMultiSelect
              label={<span>LSCs&nbsp;&nbsp;<QuestionIcon toolTipText="Leave blank to include all LSCs." /></span>}
              name="lscs"
              value={formState.lscs}
              error={errorState.lscs}
              message={errorState.lscs}
              filterIds={formState.zones.map(c => c.id).join(',')}
              onChange={(value) => {
                onFormValueChanged('lscs', value);
                onFormValueChanged('clubs', []);
              }} />
          </div>
          </>
          }
          {orgUnitState.arrayData.find(ou => ou.orgUnitId === formState.orgUnitId)?.orgLevelId !== 3 &&
          <div className="col-xs-12 col-sm-6 col-md-4">
            <ClubMultiSelect
              label={<span>Clubs&nbsp;&nbsp;<QuestionIcon toolTipText="Leave blank to include all clubs." /></span>}
              name="clubs"
              value={formState.clubs}
              error={errorState.clubs}
              message={errorState.clubs}
              filterIds={clubFilterIds}
              onChange={(value) => { onFormValueChanged('clubs', value); }} />
          </div>
          }
        </div>
        <div className="row">
          <div className="col-xs-12 col-sm-6 col-md-4">
            <Dropdown
              label={<span>Season Year&nbsp;&nbsp;<QuestionIcon toolTipText="If you select a Season Year, do not enter a date range." /></span>}
              name="season"
              options={seasonYearState.options}
              value={formState.seasonId}
              error={errorState.seasonId}
              message={errorState.seasonId}
              isLoading={seasonYearState.isLoading}
              onChange={(newValue, newValueLabel, e) => {
                e && e.target && e.target.value &&
                  onValueTextPairChanged(newValue, 'seasonId', newValueLabel, 'seasonYear', e);
              }} />
          </div>
          <div className="col-xs-12 col-sm-6 col-md-4">
            <DatePicker
              label={<span>Start Date&nbsp;&nbsp;<QuestionIcon toolTipText="If you select a Start Date, do not enter a Season Year." /></span>}
              name="startDate"
              value={formState.startDate}
              error={errorState.startDate}
              message={errorState.startDate}
              countOfYears={150}
              startYearOffset={-149}
              onChange={(value) => { updateFormState('startDate', value); }} />
          </div>
          <div className="col-xs-12 col-sm-6 col-md-4">
            <DatePicker
              label="End Date"
              name="endDate"
              value={formState.endDate}
              error={errorState.endDate}
              message={errorState.endDate}
              countOfYears={150}
              startYearOffset={-149}
              onChange={(value) => { updateFormState('endDate', value); }} />
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12 col-sm-6 col-md-4">
            <CourseMultiSelect
              label={<span>Courses&nbsp;&nbsp;<QuestionIcon toolTipText="Leave blank to include all courses." /></span>}
              name="courses"
              value={formState.courses}
              error={errorState.courses}
              message={errorState.courses}
              includeOpenWater={false}
              onChange={(value) => {
                onFormValueChanged('courses', value);
                onFormValueChanged('events', []);
              }}
            />
          </div>
          <div className="col-xs-12 col-sm-6 col-md-4">
            <MeetClassificationMultiSelect 
              label={<span>Meet Classification&nbsp;&nbsp;<QuestionIcon toolTipText="Leave blank to include all meet classifications." /></span>} 
              name="meetClassifications"
              value={formState.meetClassifications}
              error={errorState.meetClassifications}
              message={errorState.meetClassifications}
              onChange={(value) => { 
                onFormValueChanged('meetClassifications', value); 
                onFormValueChanged('meetTypes', []);
              }}
            />
          </div>  
          {formState.meetClassifications.length > 0 &&
          <div className="col-xs-12 col-sm-6 col-md-4">
            <MeetTypeMultiSelect 
              label={<span>Meet Type&nbsp;&nbsp;<QuestionIcon toolTipText="Leave blank to include all meet types." /></span>}
              name="meetTypes"
              value={formState.meetTypes}
              error={errorState.meetTypes}
              message={errorState.meetTypes}
              filterIds={formState.meetClassifications.map(c => c.id).join(',')}
              onChange={(value) => { onFormValueChanged('meetTypes', value); }}
            />
          </div>
          }
          <div className="col-xs-12 col-sm-6 col-md-4">
            <YesNoSwitch
              label={<span>No Times&nbsp;&nbsp;<QuestionIcon toolTipText="Returns Meets with No Times." /></span>}
              name="meetsNoTime"
              checked={formState.meetsNoTime === true}
              error={errorState.meetsNoTime}
              message={errorState.meetsNoTime}
              value={formState.meetsNoTime}
              onChange={(value) => { updateFormState('meetsNoTime', value); }} />
          </div>
        </div>
        <div className="row usas-extra-top-margin usas-extra-bottom-margin">
          <div className="col-xs-12 usas-extra-top-margin">
            <PrimaryButton type="button" onClick={(e) => onRunReportClicked(e)}>Run</PrimaryButton>&nbsp;
            <SecondaryButton type="button" onClick={() => onClearForm()}>Clear Form</SecondaryButton>
          </div>
        </div>
      </form>
      <PopUpModal
        className={style.reportModalTop}
        widthPct={98}
        maxWidth={2050}
        displayPopUp={modalState.displayPopUp}
        onModalCanceled={onModalCancelClicked}>
        <StandardKendoReport
          reportInfoId={REPORT_INFO_ID}
          reportParameters={reportParameters}
          title="Meet Detail Report"
          detailReportTitle="Meet Detail Report"
          parameters={parameters}
          columns={columns}
          usePagination={false}
          pageSize={10}
          enablePdfExport={true}
          enableExcelExport={true}
          gridHeight="750px"
          excelColumns={excelColumns}
        />
      </PopUpModal>
    </Fragment>
  );
}

export default MeetDetailKendoReport;